import { useState, useEffect } from "react";
import Cookies from "js-cookie";
import XPRSurveyLogo from "../../../assests/XPRSurveyGreen.png";
import { useNavigate } from "react-router-dom";
import authService from "../Services/AuthService";
import {
	FaFacebookF,
	FaTwitter,
	FaInstagram,
	FaLinkedinIn,
} from "react-icons/fa";
import XPRSurveyGreen from "../../../assests/XPRSurveyGreen.png";
import { getCaptcha } from "../Services/toolkitService";
import {
	ArrowPathIcon,
	ArrowRightIcon,
	EnvelopeIcon,
	LockClosedIcon,
	EyeIcon,
	EyeSlashIcon,
	CheckCircleIcon,
	ShieldCheckIcon,
	ChartBarIcon,
	SparklesIcon,
	RocketLaunchIcon,
} from "@heroicons/react/24/outline";
import VectorImage from "../../../assests/NewLoginVectorImage1.png";
import FooterImage from "../../../assests/FooterImage.png";
import { useUser } from "../../../Utility/userContext";

const FEATURES = [
	{
		icon: SparklesIcon,
		title: "Survival & Productivity Predictors",
		description:
			"Score applicant longevity and sales output using a channel-specific 9-box readiness grid tailored for both experienced agents and freshers.",
	},
	{
		icon: ChartBarIcon,
		title: "Grounded Insurance Scenarios",
		description:
			"Evaluate situational role-readiness against real-world metrics including WFYP, policy persistency, customer suitability, and branch dynamics.",
	},
	{
		icon: ShieldCheckIcon,
		title: "Integrity & Compliance Red-Flagging",
		description:
			"Proactively mitigate risk by detecting mis-selling, role misrepresentation, and fake documentation through zero-tolerance ethical safeguards.",
	},
];
export default function LoginPage() {
	const { refreshUser } = useUser();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [ip, setIp] = useState("");
	const [device, setDevice] = useState("Unknown");
	const navigate = useNavigate();
	const [generalError] = useState("");

	const [captcha, setCaptcha] = useState("");
	const [captchaText, setCaptchaText] = useState("");
	const [captchaImage, setCaptchaImage] = useState("");
	const [captchaInput, setCaptchaInput] = useState("");
	const [hexColor, setHexColor] = useState("#23c2a8f0");
	const [errors, setErrors] = useState({});

	const [showPassword, setShowPassword] = useState(false);

	// Auto-redirect if user already has a valid session
	useEffect(() => {
		const token = Cookies.get("JWTToken");
		if (!token) return;
		try {
			const payload = JSON.parse(atob(token.split(".")[1]));
			if (payload.exp * 1000 > Date.now()) {
				navigate("/dashboard", { replace: true });
			}
		} catch (e) {
			// Invalid token — stay on login page
		}
	}, [navigate]);

	useEffect(() => {
		fetch("https://api64.ipify.org?format=json")
			.then((res) => res.json())
			.then((data) => setIp(data.ip))
			.catch(() => setIp("0.0.0.0"));

		const userAgent = navigator.userAgent.toLowerCase();
		let deviceType = "Unknown";
		if (/windows/i.test(userAgent)) deviceType = "Windows PC";
		else if (/macintosh|mac os x/i.test(userAgent)) deviceType = "Mac";
		else if (/iphone/i.test(userAgent)) deviceType = "iPhone";
		else if (/ipad/i.test(userAgent)) deviceType = "iPad";
		else if (/android/i.test(userAgent)) deviceType = "Android";
		else if (/linux/i.test(userAgent)) deviceType = "Linux";
		else if (/tablet|playbook|silk/i.test(userAgent)) deviceType = "Tablet";
		else if (/mobile/i.test(userAgent)) deviceType = "Mobile";

		setDevice(deviceType);
	}, []);

	const handleLogin = async (e) => {
		e.preventDefault();
		setLoading(true);
		setErrors({});

		const validationErrors = validateForm();
		if (Object.keys(validationErrors).length > 0) {
			setErrors(validationErrors);
			setLoading(false);
			return;
		}

		try {
			const result = await authService.login(email, password, ip, device);
			if (result.status.toLowerCase() === "success") {
				refreshUser();
				CustomToast("success", result.message);
				setLoading(false);
				setSuccess(true);
				setTimeout(() => {
					navigate("/dashboard");
				}, 1000);
				return;
			} else {
				CustomToast("fail", result.message || "Invalid credentials");
				await generateCaptcha();
			}
		} catch (error) {
			await generateCaptcha();
			CustomToast(
				"fail",
				error.message || "Something went wrong. Please try again.",
			);
		} finally {
			setLoading(false);
		}
	};

	const validateForm = () => {
		const newErrors = {};

		if (!email.trim()) {
			newErrors.email = "Email is required.";
		} else if (!/\S+@\S+\.\S+/.test(email)) {
			newErrors.email = "Invalid email format.";
		}

		if (!password.trim()) {
			newErrors.password = "Password is required.";
		}

		if (!captchaInput.trim()) {
			newErrors.captchaInput = "Captcha is required.";
			generateCaptcha();
		} else if (captchaInput.trim() !== captchaText.trim()) {
			newErrors.captchaInput =
				"Please type the captcha exactly as shown (case-sensitive).";
			generateCaptcha();
		}
		return newErrors;
	};

	// Generate random captcha
	const generateCaptcha = async () => {
		try {
			const color = hexColor?.startsWith("#")
				? hexColor
				: `#${hexColor || "23c2a8f0"}`;
			const data = await getCaptcha(color);
			setHexColor(data.hexColor);
			setCaptchaText(data.captchaText);
			setCaptchaImage(data.captchaImage);
		} catch (error) {
			setCaptcha("ERROR");
		}
	};

	useEffect(() => {
		generateCaptcha();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isEmailValid = email.trim() !== "" && /\S+@\S+\.\S+/.test(email);

	return (
		<>
			<div className="relative isolate min-h-screen overflow-hidden bg-gradient-to-br from-[#c2a8f033] via-white to-[#c2a8f033]">
				<div className="relative z-20 h-2 w-full bg-gradient-to-r from-[#48086f] to-[#7f35b2]" />
				<div
					aria-hidden="true"
					className="pointer-events-none absolute left-[45%] top-12 hidden h-24 w-24 opacity-40 lg:block"
					style={{
						backgroundImage:
							"radial-gradient(circle, #7f35b2 1.5px, transparent 1.5px)",
						backgroundSize: "14px 14px",
					}}
				/>
				<div className="absolute inset-y-0 right-0 hidden w-full pointer-events-none lg:block z-0">
					<svg
						className="absolute right-0 h-full w-full"
						viewBox="0 0 100 100"
						preserveAspectRatio="none"
					>
						<path
							fill="#7f35b2"
							// fill="#48086f"
							fillOpacity="0.3"
							d="M55,0 C66,20 46,40 52,65 C55,78 62,90 60,100 L100,100 L100,0 Z"
						/>
					</svg>
				</div>
				<div className="relative z-10 mx-auto grid max-w-[90rem] grid-cols-1 items-center gap-8 px-4 py-8 sm:px-6 sm:py-10 lg:grid-cols-2 lg:gap-12 lg:py-16">
					<div className="hidden md:flex md:flex-col">
						<h1 className="mb-4 text-4xl font-extrabold leading-tight text-[#000000] lg:text-5xl">
							Assessment. Analyze.
							<br />
							Decide <span className="text-[#48086f]">Better</span>.
						</h1>
						<p className="mb-10 max-w-md text-gray-600">
							Collect meaningful feedback and turn every response into clear,
							actionable insight for your team.
						</p>

						<div className="relative mb-6 md:min-h-[220px] lg:min-h-[270px]">
							<div className="relative z-20 space-y-5 lg:max-w-[400px]">
								{FEATURES.map((feature, index) => (
									<div
										key={feature.title}
										className="relative flex items-start gap-4"
									>
										{index !== FEATURES.length - 1 && (
											<span className="absolute left-7 top-10 -bottom-6 w-[2px] border-l-2 border-dashed border-[#7f35b2]/40" />
										)}
										<div className="relative z-10 flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-full bg-[#c2a8f040] text-[#48086f]">
											<feature.icon className="h-8 w-8" />
										</div>
										<div className="flex flex-col pt-1">
											<h3 className="font-semibold text-gray-800 leading-tight">
												{feature.title}
											</h3>
											<p className="mt-1 text-sm text-gray-500 leading-snug">
												{feature.description}
											</p>
										</div>
									</div>
								))}
							</div>
							<img
								src={VectorImage}
								alt=""
								className="pointer-events-none relative z-10 mx-auto mt-6 w-full max-w-sm select-none md:max-w-md lg:absolute lg:right-[-270px] lg:top-[-110px] lg:mt-0 lg:mx-0 lg:max-w-[37rem]"
							/>
						</div>

						{/* Mission card */}
						<div className="flex max-w-xl items-start gap-4 rounded-2xl bg-[#c2a8f01a] p-4 shadow-md lg:p-5">
							<div className="flex h-[70px] w-[70px] shrink-0 items-end justify-center">
								<div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#c2a8f040] text-[#48086f] sm:h-[70px] sm:w-[70px]">
									<RocketLaunchIcon className="h-10 w-10" />
								</div>
							</div>
							<div>
								<h4 className="mb-1 font-semibold text-gray-800">
									Our Mission
								</h4>
								<p className="text-sm text-gray-600">
									Our mission is to help teams make sharper frontline hiring
									decisions by identifying readiness, skill gaps, and compliance
									risks early.
								</p>
							</div>
						</div>
					</div>

					<div className="flex justify-center lg:justify-end lg:pr-6">
						<div className="relative w-full max-w-md lg:max-w-lg">
							<div className="relative z-10 rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-black/5 sm:p-6 lg:p-10">
								<div className="mb-0.1 flex justify-center">
									<img
										src={XPRSurveyLogo}
										alt="XPRSurvey"
										className="mb-2.5 h-auto w-28 sm:w-32"
									/>
								</div>
								<h2 className="mb-1 text-center text-xl font-bold text-gray-900 sm:text-2xl">
									Welcome Back!
								</h2>
								<p className="mb-6 text-center text-sm text-gray-500 sm:mb-8">
									Sign in to continue to your account
								</p>
								<form onSubmit={handleLogin}>
									{/* Email */}
									<div className="mb-5">
										<label className="mb-1.5 block text-sm text-gray-700">
											Registered Email <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<EnvelopeIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
											<input
												type="email"
												value={email}
												onChange={(e) => setEmail(e.target.value)}
												placeholder="Enter your email"
												className={`w-full rounded-lg border py-2.5 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-[#7f35b2] ${errors.email ? "border-red-500" : "border-gray-300"
													}`}
											/>
											{isEmailValid && (
												<CheckCircleIcon className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#7f35b2]" />
											)}
										</div>
										{errors.email && (
											<p className="mt-1 text-sm text-red-500">
												{errors.email}
											</p>
										)}
									</div>

									{/* Password */}
									<div className="mb-5">
										<label className="mb-1.5 block text-sm text-gray-700">
											Password <span className="text-red-500">*</span>
										</label>
										<div className="relative">
											<LockClosedIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
											<input
												type={showPassword ? "text" : "password"}
												value={password}
												onChange={(e) => setPassword(e.target.value)}
												placeholder="Enter your password"
												className={`w-full rounded-lg border py-2.5 pl-10 pr-10 focus:outline-none focus:ring-2 focus:ring-[#7f35b2] ${errors.password ? "border-red-500" : "border-gray-300"
													}`}
											/>
											<button
												type="button"
												onClick={() => setShowPassword((prev) => !prev)}
												className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
												aria-label={
													showPassword ? "Hide password" : "Show password"
												}
											>
												{showPassword ? (
													<EyeSlashIcon className="h-5 w-5" />
												) : (
													<EyeIcon className="h-5 w-5" />
												)}
											</button>
										</div>
										{errors.password && (
											<p className="mt-1 text-sm text-red-500">
												{errors.password}
											</p>
										)}
									</div>

									{/* Captcha */}
									<div className="mb-6">
										<label className="mb-1.5 block text-sm text-gray-700">
											Verification Code
										</label>
										<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
											<div className="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-start">
												{captchaImage ? (
													<img
														src={captchaImage}
														alt="Captcha"
														className="h-[42px] w-[120px] shrink-0 rounded-lg border border-gray-300 object-cover"
													/>
												) : (
													<div className="flex h-[42px] w-[120px] shrink-0 select-none items-center justify-center rounded-lg border border-[#7f35b2]/30 bg-[#c2a8f033] text-base font-semibold text-[#000000]">
														{captcha || "loading"}
													</div>
												)}

												<button
													type="button"
													onClick={generateCaptcha}
													className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-gray-500 transition-colors hover:border-[#7f35b2] hover:text-[#7f35b2]"
													aria-label="Refresh captcha"
												>
													<ArrowPathIcon className="h-4 w-4" />
												</button>
											</div>

											<input
												type="text"
												placeholder="Enter Verification Code"
												value={captchaInput}
												onChange={(e) => setCaptchaInput(e.target.value)}
												disabled={loading}
												className={`min-w-0 w-full flex-1 rounded-lg border px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#7f35b2] ${errors.captchaInput
													? "border-red-500"
													: "border-gray-300"
													}`}
											/>
										</div>
										{errors.captchaInput && (
											<p className="mt-1 text-sm text-red-500">
												{errors.captchaInput}
											</p>
										)}
									</div>

									{generalError && (
										<p className="mb-4 text-center text-red-600">
											{generalError}
										</p>
									)}

									{/* Login Button */}
									<button
										type="submit"
										disabled={loading || success}
										className="flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#7f35b2] to-[#48086f] py-3 text-base font-medium text-white transition hover:opacity-90 disabled:opacity-60 sm:text-lg"
									>
										{loading ? "Logging in..." : "Login"}
										{!loading && <ArrowRightIcon className="h-4 w-4" />}
									</button>
								</form>
								{/* Footer Links */}
								<div className="flex flex-wrap justify-end gap-2 text-sm mt-3.5">
									<a href="/" className="text-[#48086f] hover:underline">
										Forgot password?
									</a>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<footer className="relative overflow-hidden bg-white">
				<div className="container relative z-10 mx-auto grid grid-cols-1 gap-8 px-4 py-10 text-center sm:px-6 md:grid-cols-2 md:text-left lg:grid-cols-4 lg:py-12">
					{/* Logo + Contact */}
					<div className="flex flex-col items-center md:items-start">
						<img
							src={XPRSurveyGreen}
							alt="Logo"
							className="w-28 mb-4 fill-[#7f35b2]"
							loading="lazy"
						/>
						<p className="text-gray-700 mb-4">info@expresscleanslate.com</p>
						<div className="flex space-x-4 text-xl text-gray-700">
							<a
								href="#"
								className="hover:text-[#1877F2] transition-colors duration-300"
							>
								<FaFacebookF />
							</a>
							<a
								href="#"
								className="hover:text-[#1DA1F2] transition-colors duration-300"
							>
								<FaTwitter />
							</a>
							<a
								href="#"
								className="hover:text-[#E4405F] transition-colors duration-300"
							>
								<FaInstagram />
							</a>
							<a
								href="#"
								className="hover:text-[#0A66C2] transition-colors duration-300"
							>
								<FaLinkedinIn />
							</a>
						</div>
					</div>

					{/* Quick Links */}
					<div className="md:border-l md:border-dashed md:border-gray-400 md:pl-6 lg:pl-8">
						<h3 className="text-lg font-semibold mb-4">Quick Links</h3>
						<ul className="space-y-2 text-gray-700">
							<li>
								<a href="#" className="!cursor-pointer hover:text-yellow-600">
									→ Home
								</a>
							</li>
							<li>
								<a href="#" className="cursor-pointer hover:text-yellow-600">
									→ SignIn
								</a>
							</li>
							<li>
								<a href="#" className="cursor-pointer hover:text-yellow-600">
									→ SignUp
								</a>
							</li>
							<li>
								<a href="#" className="cursor-pointer hover:text-yellow-600">
									→ Privacy Policy
								</a>
							</li>
							<li>
								<a href="#" className="cursor-pointer hover:text-yellow-600">
									→ Terms And Condition
								</a>
							</li>
						</ul>
					</div>

					{/* Other Services */}
					<div className="md:border-l md:border-dashed md:border-gray-400 md:pl-6 lg:pl-8">
						<h3 className="text-lg font-semibold mb-4">
							Other Services And Products
						</h3>
						<ul className="space-y-2 text-gray-700">
							<li>
								<a
									href="https://www.opt4solution.site/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ Recruitment & Staffing
								</a>
							</li>
							<li>
								<a
									href="https://www.vrsamadhantech.com/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ Tech Solutions
								</a>
							</li>
							<li>
								<a
									href="https://www.opt4solution.site/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ HR & Payroll
								</a>
							</li>
							<li>
								<a
									href="https://opt4crm.com/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ Opt4CRM Portal
								</a>
							</li>
							<li>
								<a
									href="https://vrsjobs.com/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ Job Portal
								</a>
							</li>
							<li>
								<a
									href="https://www.opt4solution.site/"
									className="cursor-pointer hover:text-yellow-600"
								>
									→ Other Solutions
								</a>
							</li>
						</ul>
					</div>
					<div className="md:border-l md:border-dashed md:border-gray-400 md:pl-6 lg:pl-8">
						<img
							src={FooterImage}
							alt=""
							className="pointer-events-none mx-auto hidden w-60 select-none lg:mx-0 lg:block lg:w-72"
						/>
					</div>
				</div>

				{/* Bottom Bar */}
				<div className="bg-gradient-to-r from-[#48086f] to-[#7f35b2] py-1.5">
					<div className="container mx-auto flex flex-col items-center justify-between gap-1 px-4 text-center text-[11px] text-white/90 sm:flex-row sm:px-6 sm:text-left">
						<span>Trusted surveys. Smarter decisions. Stronger teams.</span>
						<span>
							Design and Developed By :-{" "}
							<span className="font-semibold text-white">
								VRSamadhan Tech Solution
							</span>
						</span>
					</div>
				</div>
			</footer>
		</>
	);
}
